import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useContracts, useContractSimulations, useApprovedReports } from '../api/hooks';
import { DataTable, EmptyState, FormField, Select, TextInput } from '../components/DesignSystem';
import { PageHeader } from '../components/PageHeader';
import { formatCurrency, formatDate } from '../lib/format';

const percentage = (value: number | null | undefined) => (value == null ? '—' : `${value}%`);

const META: Record<string, [string, string]> = {
  'report-simulations': [
    'Report Simulations',
    'Select an authorised contract to review and run its reporting windows.',
  ],
  'general-ledger': ['Statement Tracker', 'Review the immutable record of approved statements.'],
  'synced-erp': ['Synced ERP', 'Prepare approved statements for your accounting workflow.'],
  'calculator-rules': [
    'Calculator Rules',
    'Review the contract terms used by the waterfall calculation.',
  ],
  insights: ['Insights', 'Forecasts, scenarios and analytics for your royalty reporting.'],
  'catalog-intelligence': [
    'Catalog Intelligence',
    'Net receipts and party allocations from approved reporting windows.',
  ],
  'contract-diff': ['Contract Diff', 'Compare the recorded terms of two authorised contracts.'],
};

function Simulations({ contractId }: { contractId: string }) {
  const query = useContractSimulations(contractId || undefined);
  if (!contractId)
    return (
      <EmptyState
        title="Select a contract"
        caption="Choose a contract above to see its report simulations."
      />
    );
  if (query.isLoading) return <p role="status">Loading simulations…</p>;
  if (query.isError) return <p role="alert">Could not load simulations. Please try again.</p>;
  return (
    <DataTable
      columns={[
        { key: 'status', header: 'Status' },
        { key: 'periodStart', header: 'From', render: (row) => formatDate(row.periodStart) },
        { key: 'periodEnd', header: 'To', render: (row) => formatDate(row.periodEnd) },
        { key: 'currency', header: 'Currency' },
        {
          key: 'open',
          header: 'Actions',
          render: (row) => <Link to={`/simulation/${row.id}`}>Open simulation</Link>,
        },
      ]}
      rows={query.data ?? []}
      rowKey={(row) => row.id}
      emptyMessage="No simulations yet. Open the contract and import sales to begin."
    />
  );
}

function ContractTools({ compare = false, rules = false }: { compare?: boolean; rules?: boolean }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const contracts = useContracts({
    status: 'authorized',
    limit: 100,
    page,
    search: search || undefined,
  });
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const rows = contracts.data?.data ?? [];
  const left = rows.find((row) => row.id === first);
  const right = rows.find((row) => row.id === second);
  return (
    <>
      <section className="rfp-card">
        <div className="rfp-filter-grid">
          <FormField htmlFor="tool-search" label="Search contracts">
            <TextInput
              id="tool-search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
                setFirst('');
                setSecond('');
              }}
              placeholder="Search catalogue…"
            />
          </FormField>
          <FormField htmlFor="tool-contract" label={compare ? 'First contract' : 'Contract'}>
            <Select
              id="tool-contract"
              value={first}
              onChange={(event) => setFirst(event.target.value)}
            >
              <option value="">Select a contract</option>
              {rows.map((row) => (
                <option value={row.id} key={row.id}>
                  {row.title}
                </option>
              ))}
            </Select>
          </FormField>
          {compare && (
            <FormField htmlFor="tool-second" label="Second contract">
              <Select
                id="tool-second"
                value={second}
                onChange={(event) => setSecond(event.target.value)}
              >
                <option value="">Select a contract</option>
                {rows
                  .filter((row) => row.id !== first)
                  .map((row) => (
                    <option value={row.id} key={row.id}>
                      {row.title}
                    </option>
                  ))}
              </Select>
            </FormField>
          )}
        </div>
        {contracts.isLoading && <p role="status">Loading contracts…</p>}
        {contracts.isError && <p role="alert">Could not load contracts. Please try again.</p>}
        <div className="rfp-toolbar">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => {
              setPage(page - 1);
              setFirst('');
              setSecond('');
            }}
          >
            Previous contracts
          </button>
          <span>Page {page}</span>
          <button
            type="button"
            disabled={!contracts.data || page * 100 >= (contracts.data.meta?.total ?? rows.length)}
            onClick={() => {
              setPage(page + 1);
              setFirst('');
              setSecond('');
            }}
          >
            Next contracts
          </button>
        </div>
      </section>
      <section className="rfp-card">
        {!compare && !rules ? (
          <Simulations contractId={first} />
        ) : !left || (compare && (!right || first === second)) ? (
          <EmptyState
            title={compare ? 'Choose two contracts to compare' : 'Select a contract'}
            caption="Review the authorised terms before running a reporting window."
          />
        ) : (
          <DataTable
            columns={[
              { key: 'term', header: 'Term' },
              { key: 'left', header: left.title },
              ...(compare ? [{ key: 'right', header: right?.title ?? 'Second contract' }] : []),
            ]}
            rows={[
              {
                term: 'Contract classification',
                left: left.contractType,
                right: right?.contractType,
              },
              {
                term: 'Reported currency',
                left: left.reportedCurrency,
                right: right?.reportedCurrency,
              },
              {
                term: 'Distribution commission',
                left: percentage(left.distributionCommissionPct),
                right: percentage(right?.distributionCommissionPct),
              },
              {
                term: 'Distribution expenses',
                left: percentage(left.distributionExpensePct),
                right: percentage(right?.distributionExpensePct),
              },
              {
                term: 'Effective date',
                left: formatDate(left.effectiveDate),
                right: formatDate(right?.effectiveDate),
              },
              {
                term: 'Expiration date',
                left: formatDate(left.expirationDate),
                right: formatDate(right?.expirationDate),
              },
              {
                term: 'Governing policy',
                left: left.governingPolicy ?? '—',
                right: right?.governingPolicy ?? '—',
              },
            ]}
            rowKey={(row) => row.term}
          />
        )}
        <p>
          <Link to="/authorised-contracts">Manage authorised contracts</Link>
        </p>
      </section>
    </>
  );
}

function StatementRecords({ catalog = false }: { catalog?: boolean }) {
  const [page, setPage] = useState(1);
  const reports = useApprovedReports({ page, limit: 25 });
  return (
    <section className="rfp-card">
      {reports.isLoading ? (
        <p role="status">Loading statements…</p>
      ) : reports.isError ? (
        <p role="alert">Could not load statements. Please try again.</p>
      ) : (
        <DataTable
          columns={[
            { key: 'reportNumber', header: 'Statement' },
            { key: 'showName', header: 'Show' },
            { key: 'approvedAt', header: 'Approved', render: (row) => formatDate(row.approvedAt) },
            {
              key: 'net',
              header: 'Net receipts',
              render: (row) => formatCurrency(row.snapshot.netTotal, row.currency),
            },
            {
              key: 'allocated',
              header: 'Allocated to parties',
              render: (row) => formatCurrency(row.snapshot.distributedTotal, row.currency),
            },
          ]}
          rows={reports.data?.data ?? []}
          rowKey={(row) => row.id}
          emptyMessage={
            catalog
              ? 'No catalog data yet — approve your first report to see it here.'
              : 'No approved statements yet.'
          }
        />
      )}
      <div className="rfp-toolbar">
        <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>
          Previous page
        </button>
        <span>Page {page}</span>
        <button
          type="button"
          disabled={
            !reports.data || page * 25 >= (reports.data.meta?.total ?? reports.data.data.length)
          }
          onClick={() => setPage(page + 1)}
        >
          Next page
        </button>
      </div>
      <p>
        <Link to="/approved-reports">View and download approved reports</Link>
      </p>
    </section>
  );
}

export function WorkspaceFeaturePage({ page }: { page: string }) {
  const [title, caption] = META[page];
  return (
    <>
      <PageHeader title={title} caption={caption} />
      {['report-simulations', 'calculator-rules', 'contract-diff'].includes(page) && (
        <ContractTools
          key={page}
          compare={page === 'contract-diff'}
          rules={page === 'calculator-rules'}
        />
      )}
      {['general-ledger', 'catalog-intelligence'].includes(page) && (
        <StatementRecords key={page} catalog={page === 'catalog-intelligence'} />
      )}
      {page === 'synced-erp' && (
        <section className="rfp-card">
          <EmptyState
            title="ERP connection not configured"
            caption="Automatic ERP mapping and sync are not available in this workspace. You can download approved statements for your accounting team."
            action={
              <Link className="rfp-button rfp-button--primary" to="/approved-reports">
                View approved statements
              </Link>
            }
          />
        </section>
      )}
      {page === 'insights' && (
        <section className="rfp-card wf-dashboard-empty">
          <EmptyState
            title="Forecasts are not available yet"
            caption="Your financial dashboard shows revenue, net receipts and payouts from approved reports."
            action={
              <Link className="rfp-button rfp-button--primary" to="/dashboard">
                View financial dashboard
              </Link>
            }
          />
        </section>
      )}
    </>
  );
}
